import React from "react";
import { useHistory } from "react-router-dom";
import {
  Button,
  ButtonVariant,
  Checkbox,
  Divider,
  TermsAgreementLabel,
  Text,
  TextField,
} from "@merge-api/merge-javascript-shared";
import AuthCard from "../../shared/AuthCard";
import CountryFooterSelector from "../../shared/CountryFooterSelector";
import GoogleSSOButton from "../../shared/GoogleSSOButton";
import { LOGIN_PATH } from "../../../../../router/RouterUtils";
import { Tenancy, getTenancy } from "../../../../../api-client/APIClient";
import { ORGANIZATION_NAME_MAX_LENGTH, signupTenantOptions } from "../../../../../constants";
import { SignupIntent } from "../../../../shared/utils/signupIntent";
import signupTenantOptionsWithIntent from "../utils/signupTenantOptionsWithIntent";

interface Props {
  email: string;
  setEmail: (v: string) => void;
  emailLocked: boolean;
  name: string;
  setName: (v: string) => void;
  nameLocked: boolean;
  organization: string;
  setOrganization: (v: string) => void;
  showOrganization: boolean;
  organizationName: string | null;
  password: string;
  setPassword: (v: string) => void;
  showPassword: boolean;
  agreedToTerms: boolean;
  setAgreedToTerms: (v: boolean) => void;
  fieldErrors: Record<string, string>;
  clearFieldError: (field: string) => void;
  isLoading: boolean;
  hideGoogleSSO: boolean;
  isSamlRequired: boolean;
  onSubmit: () => void;
  ctaGtagId?: string;
  signupIntent?: SignupIntent;
}

const DetailsStep = ({
  email,
  setEmail,
  emailLocked,
  name,
  setName,
  nameLocked,
  organization,
  setOrganization,
  showOrganization,
  organizationName,
  password,
  setPassword,
  showPassword,
  agreedToTerms,
  setAgreedToTerms,
  fieldErrors,
  clearFieldError,
  isLoading,
  hideGoogleSSO,
  isSamlRequired,
  onSubmit,
  ctaGtagId,
  signupIntent,
}: Props) => {
  const history = useHistory();
  const tenancy = getTenancy();
  const isSingleTenant = tenancy === Tenancy.SingleTenant;
  const tenantOption =
    signupTenantOptions[
      tenancy === Tenancy.EUMultiTenant ? "eu" : tenancy === Tenancy.APMultiTenant ? "apac" : "usa"
    ];

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSubmit();
  };

  const handleSignInClick = (e: React.MouseEvent) => {
    e.preventDefault();
    history.push(LOGIN_PATH, { email });
  };

  const ctaText = isSamlRequired ? "Create Account with SSO" : "Sign up";

  return (
    <AuthCard className="p-6 flex flex-col gap-6 animate-slideDown">
      <div>
        <Text variant="h3" as="h1">
          Create your Merge account
        </Text>
        {organizationName && (
          <Text variant="sm" className="text-gray-40 mt-1">
            Joining {organizationName} on Merge
          </Text>
        )}
      </div>

      <form onSubmit={handleSubmit} noValidate className="flex flex-col gap-6">
        <div>
          <TextField
            name="email"
            type="email"
            labelTitle="Work email"
            placeholder="Email"
            value={email}
            onChange={(e) => {
              setEmail(e.target.value);
              clearFieldError("email");
            }}
            error={!!fieldErrors.email}
            errorText={fieldErrors.email}
            readOnly={emailLocked}
            disabled={emailLocked}
          />
          {/*
            DX2: when backend says the email already exists, surface a "Sign in instead?"
            link. This string-matches against backend error copy — brittle. Backend should
            return a structured field error code (e.g. "EMAIL_ALREADY_REGISTERED") and we
            should key on that. Until then, keep the regex permissive across common phrasings:
            "already exists", "already registered", "is taken", "is in use".
          */}
          {fieldErrors.email &&
            /already|exists|registered|taken|in[\s_-]?use/i.test(fieldErrors.email) && (
              <Text variant="sm" className="text-red-60 mt-1">
                <a
                  href={LOGIN_PATH}
                  onClick={handleSignInClick}
                  className="underline hover:no-underline"
                >
                  Sign in instead?
                </a>
              </Text>
            )}
        </div>
        {showOrganization && (
          <TextField
            name="organization"
            type="text"
            labelTitle="Company"
            placeholder="Company"
            value={organization}
            maxLength={ORGANIZATION_NAME_MAX_LENGTH}
            onChange={(e) => {
              setOrganization(e.target.value);
              clearFieldError("organization");
            }}
            error={!!fieldErrors.organization}
            errorText={fieldErrors.organization}
          />
        )}
        <TextField
          name="name"
          type="text"
          labelTitle="Name"
          placeholder="Name"
          value={name}
          onChange={(e) => {
            setName(e.target.value);
            clearFieldError("name");
          }}
          error={!!fieldErrors.name}
          errorText={fieldErrors.name}
          readOnly={nameLocked}
          disabled={nameLocked}
        />
        {showPassword && (
          <TextField
            name="password"
            type="password"
            labelTitle="Password"
            labelSubtitle="Must be at least 8 characters"
            placeholder="Password"
            value={password}
            onChange={(e) => {
              setPassword(e.target.value);
              clearFieldError("password");
            }}
            error={!!fieldErrors.password}
            errorText={fieldErrors.password}
          />
        )}

        <Divider />

        {!hideGoogleSSO && (
          <GoogleSSOButton
            text="Sign up with Google"
            loginHint={email || undefined}
            disabled={isLoading}
          />
        )}

        <div className="flex flex-col gap-2">
          <Checkbox
            variant="secondary"
            labelAlignment="start"
            className="[&_button]:!border-black [&_button[aria-checked='true']]:!bg-black"
            checked={agreedToTerms}
            onChange={(v) => {
              setAgreedToTerms(v);
              clearFieldError("agreed_to_terms_of_service_and_read_privacy_policy");
            }}
            label={<TermsAgreementLabel variant="sm" />}
          />
          {fieldErrors.agreed_to_terms_of_service_and_read_privacy_policy && (
            <Text variant="sm" className="text-red-60">
              {fieldErrors.agreed_to_terms_of_service_and_read_privacy_policy}
            </Text>
          )}
          <Button
            type="submit"
            fullWidth
            loading={isLoading}
            variant={ButtonVariant.PrimaryGray}
            id={ctaGtagId}
          >
            {ctaText}
          </Button>
        </div>
      </form>

      <div className="flex items-center justify-between">
        <Text variant="md">
          Already have an account?{" "}
          {/* Raw <a> because we intercept with history.push to carry email in location.state (DX1) */}
          <a
            href={LOGIN_PATH}
            onClick={handleSignInClick}
            className="text-blue-60 underline hover:text-blue-80"
          >
            Sign in
          </a>
        </Text>
        {!isSingleTenant && (
          <CountryFooterSelector
            options={signupTenantOptionsWithIntent(signupIntent)}
            selectedOption={tenantOption}
          />
        )}
      </div>
    </AuthCard>
  );
};

export default DetailsStep;
